import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Ticket, Search, ShieldAlert, Ban, RotateCcw, Trash2, Eye, EyeOff, Users, X, AlertTriangle, Plus, Pencil, CalendarDays, Loader2 } from "lucide-react";

const fmtEur = (n) => `${Math.round(Number(n) || 0)} €`;

export default function AdminAffiches() {
  const [affiches, setAffiches] = useState([]);
  const [q, setQ] = useState("");
  const [onlySuspended, setOnlySuspended] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState(null); // affiche en cours de suspension
  const [offerForm, setOfferForm] = useState(null); // null | {} (création) | affiche (édition)
  const [placesSchool, setPlacesSchool] = useState(null); // {id, name}
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    api.get("/admin/schools")
      .then(({ data }) => setSchools((data || []).filter((sc) => (sc.status || "ACTIVE") === "ACTIVE")))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/affiches");
      setAffiches(data || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reactivate = async (a) => {
    try {
      await api.post(`/admin/affiches/${a.id}/suspend`, { suspended: false, reason: "" });
      toast.success("Formule réactivée.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer définitivement la formule « ${a.title} » ?`)) return;
    try {
      await api.delete(`/admin/affiches/${a.id}`);
      toast.success("Formule supprimée.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return affiches.filter((a) => {
      if (onlySuspended && !a.suspended) return false;
      if (!term) return true;
      return (
        (a.partner_name || "").toLowerCase().includes(term) ||
        (a.title || "").toLowerCase().includes(term) ||
        (a.city || "").toLowerCase().includes(term) ||
        (a.postal_code || "").includes(term)
      );
    });
  }, [affiches, q, onlySuspended]);

  const suspendedCount = affiches.filter((a) => a.suspended).length;

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <div className="flex items-center gap-2">
          <Ticket className="h-6 w-6 text-brand" />
          <h1 className="text-3xl font-extrabold tracking-tighter">Formules des auto-écoles</h1>
        </div>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <p className="max-w-3xl text-neutral-500">
            Les auto-écoles publient leurs formules, et tu peux aussi <strong>ajouter ou modifier un forfait</strong> pour
            elles et gérer leurs <strong>places par mois</strong>. Contrôle la conformité et <strong>suspends</strong> toute
            annonce non conforme (tu peux la <strong>réactiver</strong> ensuite).
          </p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setPlacesSchool({})} data-testid="admin-places-open"
              className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm font-bold text-neutral-700 hover:border-brand hover:text-brand">
              <CalendarDays className="h-4 w-4" /> Places par mois
            </button>
            <button onClick={() => setOfferForm({})} data-testid="affiche-add-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white hover:bg-brand-hover">
              <Plus className="h-4 w-4" /> Ajouter un forfait
            </button>
          </div>
        </div>

        {/* Bandeau de règles de conformité */}
        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="affiche-rules-banner">
          <div className="flex items-center gap-2 text-amber-900">
            <ShieldAlert className="h-5 w-5" />
            <h2 className="text-sm font-bold uppercase tracking-wide">Règles de publication (motifs de suspension)</h2>
          </div>
          <ul className="mt-3 space-y-1.5 text-sm text-amber-900">
            <li>• <strong>Aucune mention du nom de l’auto-école</strong> ne doit apparaître dans le titre, les options ou les détails de la formule.</li>
            <li>• Les coordonnées de l’auto-école ne sont communiquées à l’élève <strong>qu’une fois sa réservation terminée</strong>.</li>
            <li>• La commission s’applique sur le <strong>montant final réellement signé</strong> avec l’élève (même si l’auto-école négocie un montant supérieur à la formule).</li>
            <li>• En cas de manquement, l’auto-école <strong>risque d’être bannie</strong> de la plateforme.</li>
          </ul>
        </div>

        {/* Filtres */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 sm:max-w-sm">
            <Search className="h-4 w-4 text-neutral-400" />
            <input
              data-testid="affiche-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher (auto-école, formule, ville)…"
              className="w-full text-sm outline-none"
            />
          </div>
          <button
            onClick={() => setOnlySuspended((v) => !v)}
            data-testid="affiche-filter-suspended"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors ${
              onlySuspended ? "border-red-300 bg-red-600 text-white" : "border-neutral-200 bg-white text-neutral-600 hover:border-red-300"
            }`}
          >
            <Ban className="h-4 w-4" /> Suspendues ({suspendedCount})
          </button>
        </div>

        {/* Liste */}
        <div className="mt-4 overflow-x-auto rounded-2xl border border-neutral-200 bg-white">
          <table className="w-full text-sm" data-testid="admin-affiches-table">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
                <th className="px-4 py-3">Auto-école</th>
                <th className="px-4 py-3">Formule</th>
                <th className="px-4 py-3">Prix</th>
                <th className="px-4 py-3">Places/mois mises à dispo</th>
                <th className="px-4 py-3">Lieu</th>
                <th className="px-4 py-3">État</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-400">Aucune formule pour ce filtre.</td></tr>
              ) : filtered.map((a) => (
                <tr key={a.id} data-testid={`affiche-row-${a.id}`}
                  className={`border-b border-neutral-100 last:border-0 hover:bg-neutral-50 ${a.suspended ? "bg-red-50/40" : ""}`}>
                  <td className="px-4 py-3 font-medium text-neutral-700">{a.partner_name || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-neutral-800">{a.title}</div>
                    <div className="text-xs text-neutral-400">
                      {a.license_type}{a.hours ? ` · ${a.hours} h` : ""}{a.gearbox ? ` · ${a.gearbox}` : ""}
                    </div>
                    {a.suspended && a.suspend_reason && (
                      <div className="mt-1 flex items-start gap-1 text-xs text-red-600" data-testid={`affiche-reason-${a.id}`}>
                        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {a.suspend_reason}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-semibold">{fmtEur(a.price)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700" data-testid={`affiche-places-${a.id}`}>
                      <Users className="h-3.5 w-3.5" /> {a.partner_places ?? 0} place{(a.partner_places ?? 0) > 1 ? "s" : ""}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{a.city} {a.postal_code ? `(${a.postal_code})` : ""}</td>
                  <td className="px-4 py-3" data-testid={`affiche-state-${a.id}`}>
                    {a.suspended ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700"><Ban className="h-3.5 w-3.5" /> Suspendue</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700"><Eye className="h-3.5 w-3.5" /> En ligne</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setOfferForm(a)} data-testid={`affiche-edit-${a.id}`} title="Modifier le forfait"
                        className="inline-flex items-center gap-1.5 rounded-md border border-neutral-300 px-2.5 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-100">
                        <Pencil className="h-4 w-4" /> Modifier
                      </button>
                      <button onClick={() => setPlacesSchool({ id: a.partner_id, name: a.partner_name })} data-testid={`affiche-places-btn-${a.id}`} title="Places par mois de cette auto-école"
                        className="inline-flex items-center gap-1.5 rounded-md border border-sky-200 px-2.5 py-2 text-xs font-semibold text-sky-700 hover:bg-sky-50">
                        <CalendarDays className="h-4 w-4" /> Places
                      </button>
                      {a.suspended ? (
                        <button onClick={() => reactivate(a)} data-testid={`affiche-reactivate-${a.id}`}
                          title="Réactiver"
                          className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 px-2.5 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">
                          <RotateCcw className="h-4 w-4" /> Réactiver
                        </button>
                      ) : (
                        <button onClick={() => setSuspendTarget(a)} data-testid={`affiche-suspend-${a.id}`}
                          title="Suspendre pour non-conformité"
                          className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-2.5 py-2 text-xs font-semibold text-red-600 hover:bg-red-50">
                          <Ban className="h-4 w-4" /> Suspendre
                        </button>
                      )}
                      <button onClick={() => remove(a)} data-testid={`affiche-delete-${a.id}`}
                        title="Supprimer"
                        className="rounded-md border border-neutral-300 p-2 text-neutral-500 hover:bg-neutral-100 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {offerForm && (
        <OfferFormModal
          initial={offerForm}
          schools={schools}
          onClose={() => setOfferForm(null)}
          onDone={() => { setOfferForm(null); load(); }}
        />
      )}
      {placesSchool && (
        <PlacesModal
          initialSchoolId={placesSchool.id || ""}
          schools={schools}
          onClose={() => { setPlacesSchool(null); load(); }}
        />
      )}

      {suspendTarget && (
        <SuspendModal
          affiche={suspendTarget}
          onClose={() => setSuspendTarget(null)}
          onDone={() => { setSuspendTarget(null); load(); }}
        />
      )}
    </Shell>
  );
}

function SuspendModal({ affiche, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Indique le motif de non-conformité (communiqué à l'auto-école)."); return; }
    setSaving(true);
    try {
      await api.post(`/admin/affiches/${affiche.id}/suspend`, { suspended: true, reason: reason.trim() });
      toast.success("Formule suspendue — l'auto-école est notifiée.");
      onDone();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const REASONS = [
    "Le nom de l'auto-école apparaît dans la formule (interdit).",
    "Coordonnées / contact direct mentionnés dans l'annonce.",
    "Informations trompeuses ou prix non conforme.",
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="affiche-suspend-modal">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-red-600" />
            <h2 className="text-lg font-extrabold text-neutral-900">Suspendre pour non-conformité</h2>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100"><X className="h-5 w-5" /></button>
        </div>
        <p className="mt-2 text-sm text-neutral-500">
          Formule <strong>{affiche.title}</strong> — {affiche.partner_name}. Elle sera retirée du devis élève
          et l’auto-école recevra le motif par e-mail.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {REASONS.map((r) => (
            <button key={r} onClick={() => setReason(r)} type="button"
              className="rounded-full border border-neutral-200 px-3 py-1 text-xs text-neutral-600 hover:border-red-300 hover:text-red-600">
              {r}
            </button>
          ))}
        </div>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          data-testid="affiche-suspend-reason"
          placeholder="Motif de la suspension (obligatoire)…"
          className="mt-3 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-red-400"
        />
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-bold text-neutral-700 hover:border-neutral-400">Annuler</button>
          <button onClick={submit} disabled={saving} data-testid="affiche-suspend-confirm"
            className="rounded-full bg-red-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60">
            {saving ? "Suspension…" : "Suspendre la formule"}
          </button>
        </div>
      </div>
    </div>
  );
}


const GEARBOXES = ["Boîte manuelle", "Boîte automatique", "Les deux au même prix"];
const inputCls = "mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-brand";

// Création / modification d'un forfait pour le compte d'une auto-école
function OfferFormModal({ initial, schools, onClose, onDone }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(() => ({
    partner_id: initial?.partner_id || "",
    title: initial?.title || "",
    price: initial?.price ?? "",
    hours: initial?.hours ?? "",
    gearbox: initial?.gearbox || "",
    code_included: typeof initial?.code_included === "boolean" ? initial.code_included : null,
    options_text: (initial?.options || []).join("\n"),
    license_type: initial?.license_type || "Permis B",
    city: initial?.city || "",
    postal_code: initial?.postal_code || "",
    active: initial?.active !== false,
    featured: !!initial?.featured,
  }));
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Choix de l'auto-école : on reprend sa ville / son code postal.
  const pickSchool = (id) => {
    const sc = schools.find((x) => x.id === id);
    setForm((f) => ({ ...f, partner_id: id, city: sc?.city || f.city, postal_code: sc?.postal_code || f.postal_code }));
  };

  const save = async () => {
    if (!form.partner_id) { toast.error("Choisis l'auto-école."); return; }
    if (!form.title.trim()) { toast.error("Le titre du forfait est obligatoire."); return; }
    if (form.price === "" || Number(form.price) < 0) { toast.error("Indique un prix valide."); return; }
    if (form.postal_code && !/^\d{5}$/.test(form.postal_code)) { toast.error("Code postal à 5 chiffres."); return; }
    const payload = {
      partner_id: form.partner_id,
      title: form.title.trim(),
      price: Number(form.price) || 0,
      hours: parseInt(form.hours, 10) || 0,
      options: form.options_text.split("\n").map((x) => x.trim()).filter(Boolean),
      license_type: form.license_type,
      gearbox: form.gearbox,
      code_included: form.code_included,
      city: form.city.trim(),
      postal_code: form.postal_code.trim(),
      active: form.active,
      featured: form.featured,
    };
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/affiches/${initial.id}`, payload);
        toast.success("Forfait mis à jour.");
      } else {
        await api.post("/admin/affiches", payload);
        toast.success("Forfait ajouté et en ligne.");
      }
      onDone();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="offer-form-modal">
      <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-extrabold">{editing ? "Modifier le forfait" : "Ajouter un forfait"}</h3>
          <button onClick={onClose} className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2"><span className="text-xs font-semibold text-neutral-500">Auto-école</span>
            <select value={form.partner_id} onChange={(e) => pickSchool(e.target.value)} disabled={editing} data-testid="offer-school-select" className={inputCls}>
              <option value="">— Choisir une auto-école active —</option>
              {schools.map((sc) => <option key={sc.id} value={sc.id}>{`${sc.name}${sc.city ? ` · ${sc.city}` : ""}`}</option>)}
            </select>
          </label>
          <label className="block sm:col-span-2"><span className="text-xs font-semibold text-neutral-500">Titre du forfait (sans le nom de l'auto-école)</span>
            <input value={form.title} onChange={(e) => upd("title", e.target.value)} placeholder="Ex. Permis accéléré 20h" data-testid="offer-title" className={inputCls} />
          </label>
          <label className="block"><span className="text-xs font-semibold text-neutral-500">Prix (€)</span>
            <input type="number" min="0" value={form.price} onChange={(e) => upd("price", e.target.value)} data-testid="offer-price" className={inputCls} />
          </label>
          <label className="block"><span className="text-xs font-semibold text-neutral-500">Heures de conduite</span>
            <input type="number" min="0" value={form.hours} onChange={(e) => upd("hours", e.target.value)} data-testid="offer-hours" className={inputCls} />
            <span className="mt-1 block text-[11px] text-amber-700">{"Entre 10 et 20 h : badge « Idéal pour repasser » automatique (élèves qui ont raté l'examen)."}</span>
          </label>
          <label className="block"><span className="text-xs font-semibold text-neutral-500">Boîte</span>
            <select value={form.gearbox} onChange={(e) => upd("gearbox", e.target.value)} data-testid="offer-gearbox" className={inputCls}>
              <option value="">— Non précisé —</option>
              {GEARBOXES.map((g) => <option key={g} value={g}>{g}</option>)}
              {form.gearbox && !GEARBOXES.includes(form.gearbox) && <option value={form.gearbox}>{form.gearbox}</option>}
            </select>
          </label>
          <div><span className="text-xs font-semibold text-neutral-500">Code inclus ?</span>
            <div className="mt-1 flex gap-2" data-testid="offer-code-included">
              {[["Oui", true], ["Non", false]].map(([l, v]) => (
                <button key={l} type="button" onClick={() => upd("code_included", v)}
                  className={`rounded-lg border px-4 py-2 text-sm font-bold ${form.code_included === v ? "border-brand bg-brand/10 text-brand" : "border-neutral-300 text-neutral-600"}`}>{l}</button>
              ))}
            </div>
          </div>
          <label className="block sm:col-span-2"><span className="text-xs font-semibold text-neutral-500">Options (une par ligne)</span>
            <textarea rows={3} value={form.options_text} onChange={(e) => upd("options_text", e.target.value)} placeholder={"Examen inclus\nSuivi personnalisé"} data-testid="offer-options" className={inputCls} />
          </label>
          <label className="block"><span className="text-xs font-semibold text-neutral-500">Ville</span>
            <input value={form.city} onChange={(e) => upd("city", e.target.value)} data-testid="offer-city" className={inputCls} />
          </label>
          <label className="block"><span className="text-xs font-semibold text-neutral-500">Code postal</span>
            <input value={form.postal_code} maxLength={5} onChange={(e) => upd("postal_code", e.target.value.replace(/\D/g, ""))} data-testid="offer-postal" className={inputCls} />
          </label>
          <label className="flex items-center gap-2 text-sm text-neutral-700 sm:col-span-2">
            <input type="checkbox" checked={form.active} onChange={(e) => upd("active", e.target.checked)} data-testid="offer-active" className="h-4 w-4 rounded border-neutral-300 text-brand" />
            Forfait en ligne (visible dans le devis)
          </label>
        </div>
        <p className="mt-3 text-xs text-neutral-500">{"Le forfait n'apparaît aux élèves que si l'auto-école a des places ce mois-ci (bouton « Places par mois »)."}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-semibold">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="offer-save"
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white hover:bg-brand-hover disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Enregistrer" : "Ajouter le forfait"}
          </button>
        </div>
      </div>
    </div>
  );
}

const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

// Places par mois d'une auto-école, saisies par l'admin (mois en cours + 5 suivants)
function PlacesModal({ initialSchoolId, schools, onClose }) {
  const [schoolId, setSchoolId] = useState(initialSchoolId);
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [savingKey, setSavingKey] = useState("");

  const months = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      return { year: d.getFullYear(), month: d.getMonth() + 1, label: `${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`, key: `${d.getFullYear()}-${d.getMonth() + 1}` };
    });
  }, []);

  const load = useCallback(async (sid) => {
    if (!sid) { setData(null); return; }
    try {
      const { data: d } = await api.get(`/admin/schools/${sid}/availability`);
      setData(d);
      const v = {};
      (d.items || []).forEach((it) => { v[`${it.year}-${it.month}`] = String(it.places); });
      setValues(v);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  }, []);

  useEffect(() => { load(schoolId); }, [schoolId, load]);

  const usedFor = (m) => (data?.items || []).find((it) => it.year === m.year && it.month === m.month)?.assigned_used || 0;

  const save = async (m) => {
    const places = parseInt(values[m.key] || "0", 10);
    if (Number.isNaN(places) || places < 0) { toast.error("Nombre de places invalide."); return; }
    setSavingKey(m.key);
    try {
      await api.put(`/admin/schools/${schoolId}/availability`, { year: m.year, month: m.month, places });
      toast.success(`Places de ${m.label} enregistrées.`);
      load(schoolId);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setSavingKey(""); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="places-modal">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-extrabold">Places par mois</h3>
          <button onClick={onClose} className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100"><X className="h-5 w-5" /></button>
        </div>
        <label className="mt-4 block"><span className="text-xs font-semibold text-neutral-500">Auto-école</span>
          <select value={schoolId} onChange={(e) => setSchoolId(e.target.value)} data-testid="places-school-select" className={inputCls}>
            <option value="">— Choisir une auto-école —</option>
            {schools.map((sc) => <option key={sc.id} value={sc.id}>{`${sc.name}${sc.city ? ` · ${sc.city}` : ""}`}</option>)}
          </select>
        </label>
        {schoolId && data && (
          <>
            <p className="mt-2 text-xs text-neutral-500">{`Ville : ${data.city || "non renseignée"}. Les places s'additionnent avec celles des autres auto-écoles de la ville.`}</p>
            <ul className="mt-4 space-y-2">
              {months.map((m) => (
                <li key={m.key} className="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 px-3 py-2.5" data-testid={`places-row-${m.key}`}>
                  <div>
                    <p className="text-sm font-semibold capitalize text-neutral-800">{m.label}</p>
                    <p className="text-xs text-neutral-400">{usedFor(m)} place(s) déjà réservée(s)</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" value={values[m.key] ?? ""} placeholder="0"
                      onChange={(e) => setValues((v) => ({ ...v, [m.key]: e.target.value }))}
                      data-testid={`places-input-${m.key}`} className="w-20 rounded-lg border border-neutral-300 px-2 py-1.5 text-right text-sm outline-none focus:border-brand" />
                    <button onClick={() => save(m)} disabled={savingKey === m.key} data-testid={`places-save-${m.key}`}
                      className="rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white hover:bg-brand-hover disabled:opacity-60">
                      {savingKey === m.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "OK"}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
